import { MONTHS, WEEKDAYS } from '../constants';
import type { DateInfo } from '../types/common';
import { getLocale, localizePath, stripLocalePrefix, type Locale } from '../i18n';

/**
 * Parses a URL path into month and year information
 * Accepts formats like: /january-2024, /print-calendar/jan-2024, /es/print-calendar/january-2024
 */
export function parseCalendarUrl(path: string): DateInfo | null {
    const cleanPath = stripLocalePrefix(path)
        .toLowerCase()
        .replace(/print-calendar/, '')
        .replace(/^\/+/, '')
        .replace(/-calendar$/, '');
    const [monthStr, yearStr] = cleanPath.split('-');

    if (!monthStr || !yearStr) return null;

    const year = parseInt(yearStr);
    if (isNaN(year)) return null;

    const month = MONTHS.find(m => m === monthStr || m.substring(0, 3) === monthStr);
    if (!month) return null;

    return { month, year };
}

export function formatMonthUrl(month: number, year: number, locale: Locale = getLocale()): string {
    return localizePath(`/print-calendar/${MONTHS[month]}-${year}`, locale);
}

/**
 * Returns weekday names array adjusted for Monday/Sunday start
 * When startOnMonday is true: ['Mon', ..., 'Sun']
 * When startOnMonday is false: ['Sun', ..., 'Sat']
 */
export function adjustWeekdays(startOnMonday: boolean): string[] {
    return startOnMonday ? [...WEEKDAYS.slice(1), WEEKDAYS[0]] : [...WEEKDAYS];
}

/**
 * Adjusts day number (0-6) based on week start preference
 * For Monday start: Sunday (0) becomes 6, others shift left
 */
export function adjustDay(day: number, startOnMonday: boolean): number {
    return startOnMonday ? (day === 0 ? 6 : day - 1) : day;
}

/**
 * Returns the number of days in a specific month and year
 * Handles leap years automatically
 */
export function getDaysInMonth(month: number, year: number): number {
    return new Date(year, month + 1, 0).getDate();
}
